import { Component, inject, OnInit } from "@angular/core";

import { CommonModule } from "@angular/common";

import { MatDialog, MatDialogModule } from "@angular/material/dialog";

import { LoaderComponent } from "../../../shared/components/loader/loader.component";

import { GloPaginationComponent } from "../../../shared/components/pagination/pagination.component";

import { GloControlComponent } from "../../../shared/components/glo-control/glo-control.component";

import { Salon } from "../../../shared/models/salon.model";

import { API_ENDPOINTS } from "../../../shared/services/api/api-endpoints";

import { ApiService } from "../../../shared/services/api/api.service";

import { BusinessSalonContextService } from "../../../shared/services/api/business-salon-context.service";

import { environment } from "../../../../environments/environment";

import { AddSalonFormComponent } from "../add-salon/add-salon.component";

@Component({
  selector: "app-salons",

  standalone: true,

  imports: [
    CommonModule,
    MatDialogModule,
    LoaderComponent,
    GloPaginationComponent,
    GloControlComponent,
  ],

  templateUrl: "./saloons.component.html",

  styleUrls: ["./saloons.component.scss"],
})
export class SalonsComponent implements OnInit {
  private dialog = inject(MatDialog);

  private apiService = inject(ApiService);

  private salonContext = inject(BusinessSalonContextService);

  assetUrl = environment.assetBaseUrl;

  salons: Salon[] = [];

  filteredSalons: Salon[] = [];

  viewSalons: Salon[] = [];

  currentPage = 1;

  itemsPerPage = 10;

  isLoading = false;

  ngOnInit(): void {
    this.loadSalons();
  }

  /* =====================================================
     KPIs
     ===================================================== */

  get salonsWithContact(): number {
    return this.salons.filter((salon: any) => !!salon.phone && !!salon.email)
      .length;
  }

  get businessTypeCount(): number {
    const types = new Set(
      this.salons
        .map((salon: any) => salon.businessType)
        .filter((type) => !!type),
    );

    return types.size;
  }

  get totalStaff(): number {
    return this.salons.reduce((sum, salon: any) => {
      const staff = Number(salon.numberOfStaff || 0);

      return sum + (Number.isFinite(staff) ? staff : 0);
    }, 0);
  }

  /* =====================================================
     Load ALL salons
     ===================================================== */

  loadSalons(): void {
    this.isLoading = true;

    const allSalons: Salon[] = [];

    const visitedUrls = new Set<string>();

    const fetchPage = (url: string): void => {
      /*
       * Safety guard in case an API ever
       * returns the same "next" URL again.
       */
      if (visitedUrls.has(url)) {
        console.error("Salon pagination loop detected:", url);

        this.finishSalonLoad(allSalons);

        return;
      }

      visitedUrls.add(url);

      this.apiService.request<any>(url, "GET", null).subscribe({
        next: (response: any) => {
          const results: Salon[] = Array.isArray(response)
            ? response
            : (response?.results ??
              response?.data?.results ??
              response?.salons ??
              (Array.isArray(response?.data) ? response.data : []));

          allSalons.push(...results);

          const nextUrl = response?.next ?? response?.data?.next ?? null;

          if (nextUrl && !visitedUrls.has(nextUrl)) {
            fetchPage(nextUrl);

            return;
          }

          this.finishSalonLoad(allSalons);
        },

        error: (error) => {
          console.error("Error loading salons:", error);

          this.isLoading = false;
        },
      });
    };

    fetchPage(API_ENDPOINTS.salons.list);
  }

  private finishSalonLoad(salons: Salon[]): void {
    /*
     * Remove accidental duplicates by salon_id.
     */
    const uniqueSalons = Array.from(
      new Map(
        salons.map((salon: any) => [String(salon.salon_id ?? salon.id), salon]),
      ).values(),
    ) as Salon[];

    this.salons = uniqueSalons;

    this.filteredSalons = [...this.salons];

    this.currentPage = 1;

    this.updatePaginatedSalons();

    /*
     * Synchronize the global sidebar context.
     *
     * This is especially important after
     * adding, editing, or deleting a salon.
     */
    this.salonContext.setSalons(
      this.salons.map((salon: any) => ({
        salon_id: String(salon.salon_id ?? salon.id),

        name: salon.name ?? salon.salon_name ?? "Salon",

        city: salon.city ?? null,
      })),
    );

    this.isLoading = false;
  }

  /* =====================================================
     Add
     ===================================================== */

  openAddSalonModal(): void {
    const dialogRef = this.dialog.open(AddSalonFormComponent, {
      panelClass: ["crm-detail-dialog"],

      data: {
        salonId: null,
      },

      width: "760px",

      maxWidth: "100vw",

      height: "100vh",

      position: {
        right: "0",
        top: "0",
      },
    });

    dialogRef.afterClosed().subscribe((refresh) => {
      if (refresh) {
        this.loadSalons();
      }
    });
  }

  /* =====================================================
     Edit
     ===================================================== */

  openEditModal(salon: Salon): void {
    const dialogRef = this.dialog.open(AddSalonFormComponent, {
      panelClass: ["crm-detail-dialog"],

      data: {
        salonId: salon.salon_id,

        salonData: salon,
      },

      width: "760px",

      maxWidth: "100vw",

      height: "100vh",

      position: {
        right: "0",
        top: "0",
      },
    });

    dialogRef.afterClosed().subscribe((refresh) => {
      if (refresh) {
        this.loadSalons();
      }
    });
  }

  /* =====================================================
     Delete
     ===================================================== */

  deleteSalon(salon: Salon): void {
    if (!confirm(`Are you sure you want to delete "${salon.name}"?`)) {
      return;
    }

    this.isLoading = true;

    this.apiService
      .request(
        API_ENDPOINTS.salons.delete(String(salon.salon_id)),

        "DELETE",

        null,

        "Salon Deleted Successfully",
      )
      .subscribe({
        next: () => {
          /*
           * Reloading also refreshes
           * BusinessSalonContextService,
           * so a deleted salon disappears
           * from the sidebar immediately.
           */
          this.loadSalons();
        },

        error: (error) => {
          console.error("Error deleting salon:", error);

          this.isLoading = false;
        },
      });
  }

  /* =====================================================
     Search
     ===================================================== */

  onSearch(searchValue: string): void {
    const term = (searchValue || "").toLowerCase().trim();

    this.filteredSalons = term
      ? this.salons.filter((salon: any) => {
          const name = (salon.name || "").toLowerCase();

          const city = (salon.city || "").toLowerCase();

          const address = (salon.address || "").toLowerCase();

          const email = (salon.email || "").toLowerCase();

          const phone = (salon.phone || "").toLowerCase();

          const businessType = (salon.businessType || "").toLowerCase();

          return (
            name.includes(term) ||
            city.includes(term) ||
            address.includes(term) ||
            email.includes(term) ||
            phone.includes(term) ||
            businessType.includes(term)
          );
        })
      : [...this.salons];

    this.currentPage = 1;

    this.updatePaginatedSalons();
  }

  /* =====================================================
     Pagination
     ===================================================== */

  updatePaginatedSalons(): void {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;

    const endIndex = startIndex + this.itemsPerPage;

    this.viewSalons = this.filteredSalons.slice(startIndex, endIndex);
  }

  onPageChange(page: number): void {
    this.currentPage = page;

    this.updatePaginatedSalons();
  }

  onItemsPerPageChange(limit: number): void {
    this.itemsPerPage = limit;

    this.currentPage = 1;

    this.updatePaginatedSalons();
  }

  /* =====================================================
     Display helpers
     ===================================================== */

  getSalonInitial(salon: Salon): string {
    return (salon.name || "S").charAt(0).toUpperCase();
  }

  getOwnerName(salon: any): string {
    const ownerName = [salon.ownerFirstName, salon.ownerLastName]
      .filter(Boolean)
      .join(" ")
      .trim();

    return ownerName || "No owner";
  }

  getSalonValue(salon: any, fieldName: string): string {
    return String(salon?.[fieldName] || "");
  }

  formatBusinessType(value: string): string {
    if (!value) {
      return "Not set";
    }

    return value
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }
}
